import React, { FC, useEffect, useState } from "react"
import Loading from "../../../ui/Loading"
import { KTitleSpan, KDropdown, KButton, KSpan, KInput } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { handleAuthorizedRequest, users } from "../../../../apis"
import { User, UserTakedown } from "../../../../types"
import { addCommasToNumber, checkAdminPermission, mapListToCamelCase, replaceCurrencies } from "../../../../utility"
import Pagination from "../../../ui/table/Pagination"
import { toast } from "react-toastify"
import { ADMIN_PERMISSIONS, errorFilledToastOptions, successFilledToastOptions } from "../../../../constants"
import AdminUserTakedownMenu from "./menu/AdminUserTakedownMenu"
import AdminTakedownMetadataModal from "./modal/AdminTakedownMetadataModal"
import { connect } from "react-redux"
import { RootState } from "../../../../store"

const TakedownStatusOptionList: KSelectOption[] = [
  { label: "Not Started", value: 1, value2: "not_started" },
  { label: "In Progress", value: 2, value2: "in_progress" },
  { label: "Failed", value: 3, value2: "failed" },
  { label: "Success", value: 4, value2: "success" }
]

const CommonOptionList: KSelectOption[] = [
  { label: "All", value: 0 },
  { label: "Yes", value: 1 },
  { label: "No", value: -1 }
]

interface UserTakedownFilterType {
  selectedArtistUploadStatus?: KSelectOption
  selectedTakedownMetadataStatus?: KSelectOption
  selectedTakedownAssetStatus?: KSelectOption
  selectedAuthenticatedLC?: KSelectOption
  selectedContractSigned?: KSelectOption
  confirmedOfferId: string
}

const initialUserTakedownFilter: UserTakedownFilterType = {
  selectedArtistUploadStatus: undefined,
  selectedTakedownMetadataStatus: undefined,
  selectedTakedownAssetStatus: undefined,
  selectedAuthenticatedLC: CommonOptionList[0],
  selectedContractSigned: CommonOptionList[0],
  confirmedOfferId: ""
}

const columnWidths: any = {
  ID: "3%",
  CREATED: "7%",
  USER_ID: "5.5%",
  ASSET_DRIVE_ID: "6%",
  ALBUM_COUNT: "6%",
  CONTRACT_SIGNED: "7%",
  LABEL_ADDED: "5%",
  SIGNUP_LC: "5%",
  ARTIST_UPLOAD_STATUS: "9%",
  METADATA_STATUS: "10.5%",
  ASSET_STATUS: "8.5%",
  DEAL_ID: "4.5%",
  OFFER_ID: "5.5%",
  METADATA_PROGRESS: "6.5%",
  ASSET_PROGRESS: "7%",
  ACTION: "4%"
}

type AdminUserTakedownProps = {
  userAdminPermissions?: string
}

const AdminUserTakedown: FC<AdminUserTakedownProps> = (props) => {
  const [userOptions, setUserOptions] = useState<KSelectOption[]>([])
  const [searchUser, setSearchUser] = useState<boolean>(false)
  const [loading, setLoading] = useState(false)
  const [selectedUser, setSelectedUser] = useState<KSelectOption>()
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>()
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(10)
  const [userTakedownList, setUserTakedownList] = useState<UserTakedown[]>([])
  const [count, setCount] = useState(0)
  const [filter, setFilter] = useState<UserTakedownFilterType>({ ...initialUserTakedownFilter })
  const [selectedMetadataAlbumIds, setSelectedMetadataAlbumIds] = useState<string[]>()
  const [selectedMetadataUser, setSelectedMetadataUser] = useState<User>()
  const [selectedUserTakedown, setSelectedUserTakedown] = useState<UserTakedown>()

  const fetchUserTakedownList = () => {
    const userIdParam = selectedUser ? `&user_id=${selectedUser.value}` : ""
    const confirmedOfferIdIdParam = filter.confirmedOfferId ? `&confirmed_offer_id=${filter.confirmedOfferId}` : ""
    const artistUploadStatusParam = filter.selectedArtistUploadStatus
      ? `&artist_upload_status=${filter.selectedArtistUploadStatus.value2}`
      : ""
    const takedownMetadataStatusParam = filter.selectedTakedownMetadataStatus
      ? `&takedown_metadata_status=${filter.selectedTakedownMetadataStatus.value2}`
      : ""
    const takedownAssetStatusParam = filter.selectedTakedownAssetStatus
      ? `&takedown_asset_status=${filter.selectedTakedownAssetStatus.value2}`
      : ""
    const contractSignedParam = filter.selectedContractSigned
      ? `&contract_signed=${filter.selectedContractSigned.value}`
      : ""
    const authenticatedLcParam = filter.selectedAuthenticatedLC
      ? `&authenticated_lc=${filter.selectedAuthenticatedLC.value}`
      : ""

    setLoading(true)
    users(
      `/list_user_takedown/?offset=${offset}&limit=${limit}${userIdParam}${confirmedOfferIdIdParam}${artistUploadStatusParam}${takedownMetadataStatusParam}${takedownAssetStatusParam}${contractSignedParam}${authenticatedLcParam}`,
      "GET"
    ).then((resp) => {
      setUserTakedownList(mapListToCamelCase(resp.data.results))
      setCount(resp.data.count)
      setLoading(false)
    })
  }

  useEffect(() => {
    fetchUserTakedownList()
  }, [offset])

  const onSearchTextChanged = async (text: string) => {
    if (!text) return
    setUserOptions([])
    clearTimeout(timeoutId)
    setTimeoutId(
      setTimeout(async () => {
        await handleAuthorizedRequest(async () => {
          const response = await users(`/?limit=10&offset=0&username=${text}`, "get")
          setUserOptions(
            response.data.results.map((user: User) => ({
              value: user.id,
              label: user.username
            }))
          )
        }, "")
      }, 500)
    )
  }

  const onClickPagination = (pageNumber: number) => {
    setOffset((pageNumber - 1) * limit)
  }

  const onTakedownCatalogMetadata = (userTakedown: UserTakedown) => {
    users(`/user_takedown_start_metadata/`, "POST", { user_id: userTakedown.userId, id: userTakedown.id }).then(
      (resp) => {
        if (!resp.data.results || resp.data.results.length === 0) {
          toast.error(`Error: ${resp.data.message}`, errorFilledToastOptions)
          return
        }
        toast.success(resp.data.message, successFilledToastOptions)

        setSelectedMetadataAlbumIds(resp.data.results)
        setSelectedMetadataUser(userTakedown.user)
        setSelectedUserTakedown(userTakedown)
      }
    )
  }

  const onTakedownCatalogAudioAsset = (userTakedown: UserTakedown) => {
    let failOnly = true
    const userName = `${userTakedown.user.firstName} ${userTakedown.user.lastName} (${userTakedown.user.email})`
    const userMembersText =
      userTakedown.userMembers?.length > 0
        ? `${userTakedown.userMembers
            .map((userMember) => `${userMember.firstName} ${userMember.lastName} (${userMember.email})`)
            .join("\n")}`
        : ""
    const includedUserText = `\n\nIncluded User(s):\n${userName}\n`
    const failOnlyPrompt = window.prompt(
      `Going to upload audio assets from drive.${includedUserText}${userMembersText}\n\nSelect upload type.\nType '1' for all, '2' for fail only. By default fail only`
    )
    if (failOnlyPrompt === null) {
      return
    }
    if (failOnlyPrompt === "1") {
      failOnly = false
    }

    users(`/user_takedown_start_asset_upload/`, "POST", {
      user_id: userTakedown.userId,
      id: userTakedown.id,
      fail_only: failOnly
    }).then((resp) => {
      if (!resp.data.success) {
        toast.error(`Error: ${resp.data.message}`, errorFilledToastOptions)
        return
      }
      toast.success(resp.data.message, successFilledToastOptions)
    })
  }

  const closeMetadataModal = (refreshList: boolean) => {
    setSelectedMetadataAlbumIds(undefined)
    setSelectedMetadataUser(undefined)
    setSelectedUserTakedown(undefined)
    if (refreshList) {
      fetchUserTakedownList()
    }
  }

  const renderUserTakedown = () => {
    return (
      <div className="pt-4 pb-4" style={{ display: "flex", flexDirection: "column" }}>
        <div className="flex pb-2" style={{ width: "100%" }}>
          {Object.keys(columnWidths).map((header) => {
            return (
              <div key={`header_${header}`} style={{ width: columnWidths[header] }}>
                <KSpan fontWeight={500} text={header.replaceAll("_", " ")} />
              </div>
            )
          })}
        </div>
        {userTakedownList.map((takedown) => {
          return (
            <div style={{ width: "100%" }} className="pt-5">
              <div
                key={`takedown_${takedown.id}`}
                className="flex pt-2"
                style={{ width: "100%", borderTop: "1px dashed grey" }}
              >
                <div style={{ width: columnWidths.ID }} className="flex gap-1 items-center">
                  <KSpan text={takedown.id?.toString() || "-"} />
                </div>
                <div style={{ width: columnWidths.CREATED }} className="flex gap-1 items-center">
                  <KSpan text={takedown.created?.substring(0, 10) || "-"} />
                </div>
                <div
                  style={{ width: columnWidths.USER_ID }}
                  className="flex gap-1 cursor-pointer items-center"
                  onClick={() =>
                    window.open(`https://artists.kahuna.io/admin/user-asset?user_id=${takedown.userId}`, "_blank")
                  }
                >
                  <KSpan text={takedown.userId?.toString() || "-"} />
                  <img src="/reports_icons/link-external.svg" width={12} />
                </div>
                <div
                  style={{ width: columnWidths.ASSET_DRIVE_ID }}
                  className="flex gap-1 cursor-pointer items-center"
                  onClick={() => window.open(`https://drive.google.com/drive/folders/${takedown.assetDrive}`, "_blank")}
                >
                  {takedown.assetDrive ? <img src={"/catalog_icons/drive_icon.png"} width={20} /> : <KSpan text="-" />}
                </div>
                <div
                  style={{ width: columnWidths.ALBUM_COUNT }}
                  className="flex gap-1 cursor-pointer items-center"
                  onClick={() => {
                    navigator.clipboard.writeText(takedown.albumIds)
                    toast.success("Album IDs copied to clipboard.", successFilledToastOptions)
                  }}
                >
                  <KSpan
                    text={takedown.albumIds
                      .split(",")
                      .filter((albumId) => albumId.length > 20)
                      .length.toString()}
                  />
                  <KSpan fontSize={10} text="/" />
                  <KSpan text={takedown.albumIds.split(",").length.toString()} />
                  <img src="/reports_icons/copy-clipboard.svg" width={12} />
                </div>
                <div style={{ width: columnWidths.CONTRACT_SIGNED }} className="flex gap-1 items-center">
                  <KSpan
                    text={takedown.contractSigned?.toString() || "-"}
                    color={takedown.contractSigned ? "" : "#E52B2B"}
                    fontWeight={takedown.contractSigned ? 400 : 700}
                  />
                </div>
                <div style={{ width: columnWidths.LABEL_ADDED }} className="flex gap-1 items-center">
                  <KSpan
                    text={takedown.labelCreated?.toString() || "-"}
                    color={takedown.labelCreated ? "" : "#E52B2B"}
                    fontWeight={takedown.labelCreated ? 400 : 700}
                  />
                </div>
                <div style={{ width: columnWidths.SIGNUP_LC }} className="flex gap-1 items-center">
                  <KSpan
                    text={takedown.authenticatedLc?.toString() || "-"}
                    color={takedown.authenticatedLc ? "" : "#E52B2B"}
                    fontWeight={takedown.authenticatedLc ? 400 : 700}
                  />
                </div>

                <div style={{ width: columnWidths.ARTIST_UPLOAD_STATUS }} className="flex gap-1 items-center">
                  <KSpan
                    text={takedown.artistUploadStatus?.toString() || "-"}
                    color={takedown.artistUploadStatus !== "not_started" ? "" : "#E52B2B"}
                    fontWeight={takedown.artistUploadStatus !== "not_started" ? 400 : 700}
                  />
                </div>
                <div style={{ width: columnWidths.METADATA_STATUS }} className="flex gap-1 items-center">
                  <KSpan
                    text={takedown.takedownMetadataStatus?.toString() || "-"}
                    color={takedown.takedownMetadataStatus !== "not_started" ? "" : "#E52B2B"}
                    fontWeight={takedown.takedownMetadataStatus !== "not_started" ? 400 : 700}
                  />
                </div>
                <div style={{ width: columnWidths.ASSET_STATUS }} className="flex gap-1 items-center">
                  <KSpan
                    text={takedown.takedownAssetStatus?.toString() || "-"}
                    color={takedown.takedownAssetStatus !== "not_started" ? "" : "#E52B2B"}
                    fontWeight={takedown.takedownAssetStatus !== "not_started" ? 400 : 700}
                  />
                </div>
                <div style={{ width: columnWidths.DEAL_ID }} className="flex gap-1 items-center">
                  <KSpan text={takedown.dealId?.toString() || "-"} />
                </div>
                <div
                  style={{ width: columnWidths.OFFER_ID }}
                  className="flex gap-1 cursor-pointer items-center"
                  onClick={() =>
                    window.open(`https://scouting.kahuna.io/confirmed-offers?id=${takedown.confirmedOfferId}`, "_blank")
                  }
                >
                  <KSpan text={takedown.confirmedOfferId?.toString() || "-"} />
                  <img src="/reports_icons/link-external.svg" width={12} />
                </div>
                <div style={{ width: columnWidths.METADATA_PROGRESS }} className="flex gap-1 items-center">
                  <KSpan
                    text={`${takedown.takedownMetadataProgress?.toString() || "-"}%`}
                    color={takedown.takedownMetadataProgress === 100 ? "#45D865" : ""}
                    fontWeight={takedown.takedownMetadataProgress === 100 ? 700 : 400}
                  />
                </div>
                <div
                  style={{ width: columnWidths.ASSET_PROGRESS }}
                  className="flex gap-1 items-center cursor-pointer"
                  onClick={() => {
                    let message = `FAIL\n`
                    message = `${message}${takedown.takedownAssetUploadFail?.replaceAll(",", "\n") || ""}`
                    message = `${message}\n\nSUCCESS\n`
                    message = `${message}${takedown.takedownAssetUploadSuccess?.replaceAll(",", "\n") || ""}`
                    navigator.clipboard.writeText(message)
                    toast.success("Details copied to clipboard", successFilledToastOptions)
                  }}
                >
                  <KSpan
                    text={`${takedown.takedownAssetProgress?.toString() || "-"}%`}
                    color={takedown.takedownAssetProgress === 100 ? "#45D865" : ""}
                    fontWeight={takedown.takedownAssetProgress === 100 ? 700 : 400}
                  />
                  <KSpan text={takedown.takedownAssetUploadResult || ""} />
                </div>
                <div style={{ width: columnWidths.ACTION }} className="flex gap-1 items-center">
                  {checkAdminPermission(ADMIN_PERMISSIONS.USER_TAKEDOWN_WRITE, props.userAdminPermissions) && (
                    <AdminUserTakedownMenu
                      selectedTakedown={takedown}
                      fetchUserTakedownList={fetchUserTakedownList}
                      onTakedownCatalogMetadata={onTakedownCatalogMetadata}
                      onTakedownCatalogAudioAsset={onTakedownCatalogAudioAsset}
                      userAdminPermissions={props.userAdminPermissions}
                    />
                  )}
                </div>
              </div>
              <div className="flex pt-2" style={{ width: "100%", borderTop: "1px solid #11111116" }}>
                <div className="flex gap-1" style={{ width: "25%" }}>
                  <KSpan fontWeight={500} color="#111" text={takedown.user.firstName} />
                  <KSpan fontWeight={500} color="#111" text={takedown.user.lastName} />
                  <KSpan fontWeight={500} color="#111" text={`(${takedown.user.email})`} />
                </div>
                <div className="flex gap-2" style={{ width: "17%" }}>
                  <KSpan text="Label:" />
                  <KSpan fontWeight={500} color="#111" text={`${takedown.user.label || "-"}`} />
                </div>
                <div className="flex gap-2" style={{ width: "10%" }}>
                  <KSpan text="Type:" />
                  <KSpan fontWeight={500} color="#111" text={`${takedown.userInformation?.type || "-"}`} />
                </div>
                <div className="flex gap-2" style={{ width: "13%" }}>
                  <KSpan text="Country:" />
                  <KSpan fontWeight={500} color="#111" text={`${takedown.userInformation?.country || "-"}`} />
                </div>
                {takedown.userInformation?.type === "individual" ? (
                  <div className="flex gap-2" style={{ width: "30%" }}>
                    <KSpan text="Birthday:" />
                    <KSpan
                      fontWeight={500}
                      color="#111"
                      text={`${takedown.userInformation?.birthday?.substring(0, 7) || "-"}`}
                    />
                  </div>
                ) : (
                  <div className="flex gap-2" style={{ width: "30%" }}>
                    <KSpan text="TaxId:" />
                    <KSpan fontWeight={500} color="#111" text={`${takedown.userInformation?.taxId || "-"}`} />
                    <KSpan text="RegNo:" />
                    <KSpan
                      fontWeight={500}
                      color="#111"
                      text={`${takedown.userInformation?.companyRegistrationNo || "-"}`}
                    />
                  </div>
                )}
                <div className="flex gap-2 items-center" style={{ width: "5%" }}>
                  <KSpan text="Addr:" />
                  {takedown.userInformation?.address && (
                    <div
                      className="cursor-pointer"
                      onClick={() => {
                        toast.success("Address copied to clipboard")
                        navigator.clipboard.writeText(takedown.userInformation?.address || "")
                      }}
                    >
                      <img src="/reports_icons/copy-clipboard.svg" width={12} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8 pt-4">
      {loading && <Loading />}
      <div className="flex justify-between items-center mb-6">
        <KTitleSpan fontSize={20} lineHeight="28px" text={"User Takedown"} />
        <div className="flex gap-2">
          <div>
            <KSpan text="Offer Id" />
            <KInput
              placeholder="Type.."
              width={80}
              value={filter.confirmedOfferId}
              onChange={(value) => setFilter({ ...filter, confirmedOfferId: value })}
            />
          </div>
          <div>
            <KSpan text="LC Account" />
            <KDropdown
              width={100}
              isClearable
              selected={filter.selectedAuthenticatedLC}
              options={CommonOptionList}
              onSelect={(selected) => setFilter({ ...filter, selectedAuthenticatedLC: selected as KSelectOption })}
              placeholder="All"
            />
          </div>
          <div>
            <KSpan text="Signed" />
            <KDropdown
              width={100}
              isClearable
              selected={filter.selectedContractSigned}
              options={CommonOptionList}
              onSelect={(selected) => setFilter({ ...filter, selectedContractSigned: selected as KSelectOption })}
              placeholder="All"
            />
          </div>
          <div>
            <KSpan text="Artist Upload Status" />
            <KDropdown
              width={180}
              isClearable
              selected={filter.selectedArtistUploadStatus}
              options={TakedownStatusOptionList}
              onSelect={(selected) => setFilter({ ...filter, selectedArtistUploadStatus: selected as KSelectOption })}
              placeholder="All"
            />
          </div>
          <div>
            <KSpan text="Metadata Status" />
            <KDropdown
              width={180}
              isClearable
              selected={filter.selectedTakedownMetadataStatus}
              options={TakedownStatusOptionList}
              onSelect={(selected) =>
                setFilter({ ...filter, selectedTakedownMetadataStatus: selected as KSelectOption })
              }
              placeholder="All"
            />
          </div>
          <div>
            <KSpan text="Audio Asset Status" />
            <KDropdown
              width={180}
              isClearable
              selected={filter.selectedTakedownMetadataStatus}
              options={TakedownStatusOptionList}
              onSelect={(selected) =>
                setFilter({ ...filter, selectedTakedownMetadataStatus: selected as KSelectOption })
              }
              placeholder="Audio Asset Status"
            />
          </div>
          <div>
            <KSpan text="User" />
            {searchUser ? (
              <div
                className="relative"
                style={{ width: 250 }}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setSearchUser(false)
                  }
                }}
              >
                <KDropdown
                  placeholder={"Search user to filter"}
                  width={240}
                  isClearable
                  selected={selectedUser}
                  onInputChange={onSearchTextChanged}
                  options={userOptions}
                  onSelect={(selectedOption) => {
                    setSelectedUser(selectedOption as KSelectOption)
                  }}
                />
              </div>
            ) : (
              <div className="">
                <KButton
                  leftIcon="/reports_icons/user.svg"
                  gap="4px"
                  text="Search user"
                  height="36px"
                  width="auto"
                  background="#F7F7F7"
                  hoverBackground="#F3F3F3"
                  activeBackground="#F7F7F7"
                  padding="8px"
                  shadowDisabled
                  onClick={() => {
                    setSearchUser(true)
                  }}
                />
              </div>
            )}
          </div>
          <div className="mt-6">
            <KButton
              height="35px"
              text="Apply Filters"
              background="black"
              textColor="white"
              onClick={() => fetchUserTakedownList()}
            />
          </div>
        </div>
      </div>
      {renderUserTakedown()}
      <AdminTakedownMetadataModal
        handleClose={closeMetadataModal}
        selectedAlbumIds={selectedMetadataAlbumIds}
        selectedUser={selectedMetadataUser}
        selectedUserTakedown={selectedUserTakedown}
      />
      <div>
        <Pagination
          key={""}
          hidden={false}
          size={"sm"}
          pageCount={Math.ceil(count / limit)}
          currentPage={offset / limit + 1}
          onClickPage={onClickPagination}
        />
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    userAdminPermissions: state.auth.user.adminPermissions
  }
}

export default connect(mapStateToProps, {})(AdminUserTakedown)
